import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { uploadExcelFile } from 'common/helpers/file';
import { ISubdivisionShort } from 'common/models';
import { AppDispatch, RootState } from 'app/store';
import {
  IBasket,
  IBasketDelivery,
  IBasketDeliveryPayload,
  IBasketItem,
  IBasketListParams,
  IBasketListSubdivisionsParams,
  ICreateBasketRequestPayload,
  IBasketStore,
  IBasketStoresPayload,
  IUpdateBasketPayload,
} from 'entities/Basket/Basket.models';
import { basketTransport } from 'entities/Basket/Basket.transport';
import { patchGoodListItemGoodInBasket } from 'entities/Goods/Good.slice';

interface IState {
  currentBasket: IBasket | null;
  basketList: IBasketItem[];
  basketCount: number;
  basket: IBasket | null;
  isBasketLoading: boolean;
  basketStores: IBasketStore[];
  basketDelivery: IBasketDelivery;
  basketSubdivisions: ISubdivisionShort[];
}

const initialState: IState = {
  currentBasket: null,
  basketList: [],
  basketCount: 0,
  basket: null,
  isBasketLoading: false,
  basketStores: [],
  basketDelivery: {
    deliveryPrice: 0,
    deliveryPriceWithTaxes: 0,
    totalWeight: 0,
  },
  basketSubdivisions: [],
};

export const basketSlice = createSlice({
  name: 'basket',
  initialState,
  reducers: {
    setCurrentBasket(state, action: PayloadAction<IBasket | null>) {
      state.currentBasket = action.payload;
    },
    setBasketList(state, action: PayloadAction<IBasketItem[]>) {
      state.basketList = action.payload;
    },
    setBasketListChunk(state, action: PayloadAction<IBasketItem[]>) {
      state.basketList.push(...action.payload);
    },
    setBasketCount(state, action: PayloadAction<number>) {
      state.basketCount = action.payload;
    },
    setBasket(state, action: PayloadAction<IBasket | null>) {
      state.basket = action.payload;

      if (state.currentBasket?.id === action.payload?.id) {
        state.currentBasket = action.payload;
      }
    },
    setIsBasketLoading(state, action: PayloadAction<boolean>) {
      state.isBasketLoading = action.payload;
    },
    setBasketStores(state, action: PayloadAction<IBasketStore[]>) {
      state.basketStores = action.payload;
    },
    setBasketDelivery(state, action: PayloadAction<IBasketDelivery>) {
      state.basketDelivery = action.payload;
    },
    setBasketSubdivisions(state, action: PayloadAction<ISubdivisionShort[]>) {
      state.basketSubdivisions = action.payload;
    },
  },
});

export const {
  setCurrentBasket,
  setBasketList,
  setBasketListChunk,
  setBasketCount,
  setBasket,
  setIsBasketLoading,
  setBasketStores,
  setBasketDelivery,
  setBasketSubdivisions,
} = basketSlice.actions;

export const selectCurrentBasket = (state: RootState) => state.basket.currentBasket;
export const selectBasket = (state: RootState) => state.basket.basket;
export const selectBasketList = (state: RootState) => state.basket.basketList;
export const selectBasketCount = (state: RootState) => state.basket.basketCount;
export const selectIsBasketLoading = (state: RootState) => state.basket.isBasketLoading;
export const selectBasketStores = (state: RootState) => state.basket.basketStores;
export const selectBasketDelivery = (state: RootState) => state.basket.basketDelivery;
export const selectBasketSubdivisions = (state: RootState) => state.basket.basketSubdivisions;

export const basketReducer = basketSlice.reducer;

export const getBasketList = (params: IBasketListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.get(params);
    dispatch(setBasketList(response.data));
    dispatch(setBasketCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getBasketListChunk = (params: IBasketListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.get(params);
    dispatch(setBasketListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getCurrentBasket = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.getById(id);
    dispatch(setCurrentBasket(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getBasketById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.getById(id);
    dispatch(setBasket(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const updateBasket = (payload: IUpdateBasketPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.update(payload);
    dispatch(setBasket(response));
    dispatch(patchGoodListItemGoodInBasket(response.goods));
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const createBasketRequest = (payload: ICreateBasketRequestPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.createRequest(payload);
    dispatch(setBasket(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const uploadBasketExcel = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.uploadExcel(id);
    uploadExcelFile(response, `basket_${id}`);
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getBasketStores = (payload: IBasketStoresPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.getStores(payload);
    dispatch(setBasketStores(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getBasketDelivery = (payload: IBasketDeliveryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.getDelivery(payload);
    dispatch(setBasketDelivery(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};

export const getBasketSubdivisions = (params: IBasketListSubdivisionsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsBasketLoading(true));

  try {
    const response = await basketTransport.getSubdivisions(params);
    dispatch(setBasketSubdivisions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsBasketLoading(false));
  }
};
